/**
 * Constants for cross-frame messaging between FinSuite (parent) and embedded applications (child iframe).
 *
 * This implements a handshake protocol:
 * 1. Embedded app sends FINSUITE_EMBED_READY to parent on mount
 * 2. Parent tracks readiness and uses postMessage for navigation instead of iframe remount
 * 3. Embedded app listens for commands (e.g., NAVIGATE_HOME) and routes internally
 *
 * Any embedded application that implements this protocol will benefit from seamless
 * navigation without iframe destruction/recreation.
 */

/** Message types sent FROM the embedded app TO the parent (FinSuite) */
export const EMBED_TO_PARENT_MESSAGES = {
  /** Signals that the embedded app is ready to receive navigation commands */
  EMBED_READY: 'FINSUITE_EMBED_READY',
  /** Reports the app's current in-app path (relative: pathname+search+hash) so the parent can
   *  deep-link its external "open in new tab" link and reflect the path in its URL hash.
   *  Payload: { path: string }. Embed plumbing params (embed/source/userAlias/isDarkMode) are
   *  stripped before sending. */
  CURRENT_PATH: 'FINSUITE_CURRENT_PATH'
} as const;

/** Message types sent FROM the parent (FinSuite) TO the embedded app */
export const PARENT_TO_EMBED_MESSAGES = {
  /** Commands the embedded app to navigate to its home/landing page */
  NAVIGATE_HOME: 'FINSUITE_NAVIGATE_HOME',
  /** Commands the embedded app to navigate to a specific relative path (deep-link restore from
   *  the FinSuite URL hash). Payload: { path: string }. Generalization of NAVIGATE_HOME. */
  NAVIGATE_TO: 'FINSUITE_NAVIGATE_TO',
  /** Commands the embedded app to switch between light and dark mode. Payload: { isDarkMode: boolean } */
  THEME_CHANGE: 'FINSUITE_THEME_CHANGE'
} as const;

/** Union type of all message types for type-safe message handling */
export type EmbedToParentMessageType = (typeof EMBED_TO_PARENT_MESSAGES)[keyof typeof EMBED_TO_PARENT_MESSAGES];
export type ParentToEmbedMessageType = (typeof PARENT_TO_EMBED_MESSAGES)[keyof typeof PARENT_TO_EMBED_MESSAGES];

/** Message sent FROM the embedded app TO the parent (FinSuite) */
export interface EmbedToParentMessage {
  type: EmbedToParentMessageType;
  payload?: Record<string, unknown>;
}

/** Message sent FROM the parent (FinSuite) TO the embedded app */
export interface ParentToEmbedMessage {
  type: ParentToEmbedMessageType;
  payload?: Record<string, unknown>;
}
